/* "Saved for your next order" list (#306).

   Renders the localStorage stash written by product-form.js when an
   add-to-cart is blocked by the one-entity-per-cart rule. Product data is
   fetched live from /products/<handle>.js so titles, prices and images can
   never go stale, and money is formatted with the shop's own format string. */
(function () {
const SAVED_KEY = 'blachford:next-order';

if (!customElements.get('saved-for-later')) {
  customElements.define(
    'saved-for-later',
    class SavedForLater extends HTMLElement {
      connectedCallback() {
        this.rows = this.querySelector('[data-saved-rows]');
        this.render();
        // Keep multiple instances (cart page + drawer) in sync
        this.onSaved = () => this.render();
        document.addEventListener('saved-for-later:change', this.onSaved);
        window.addEventListener('storage', this.onSaved);
      }

      disconnectedCallback() {
        document.removeEventListener('saved-for-later:change', this.onSaved);
        window.removeEventListener('storage', this.onSaved);
      }

      readStash() {
        try {
          const stash = JSON.parse(localStorage.getItem(SAVED_KEY) || 'null');
          if (!stash || !Array.isArray(stash.items) || stash.items.length === 0) return null;
          return stash;
        } catch (e) {
          return null;
        }
      }

      writeStash(stash) {
        try {
          if (!stash || stash.items.length === 0) {
            localStorage.removeItem(SAVED_KEY);
          } else {
            localStorage.setItem(SAVED_KEY, JSON.stringify(stash));
          }
        } catch (e) {
          /* storage unavailable */
        }
        document.dispatchEvent(new CustomEvent('saved-for-later:change'));
      }

      money(cents) {
        const format =
          (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}';
        const amount = (cents / 100).toFixed(2);
        const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return format.replace(/\{\{\s*amount[^}]*\}\}/, withCommas);
      }

      async render() {
        const stash = this.readStash();
        if (!stash) {
          this.hidden = true;
          if (this.rows) this.rows.innerHTML = '';
          return;
        }

        // Resolve each saved line against live product data
        const resolved = await Promise.all(
          stash.items.map(async (item) => {
            if (!item.handle) return null;
            try {
              // routes.root_url is "/" on a single-locale shop and "/en-ca/" when
              // localized; strip the trailing slash so we never emit "//products/…"
              // (a protocol-relative URL pointing at the host "products").
              const root = (window.routes && window.routes.root_url) || '/';
              const base = root.endsWith('/') ? root.slice(0, -1) : root;
              const res = await fetch(`${base}/products/${item.handle}.js`);
              if (!res.ok) return null;
              const product = await res.json();
              const variant = (product.variants || []).find((v) => v.id === item.id);
              if (!variant) return null;
              return { item: item, product: product, variant: variant };
            } catch (e) {
              return null;
            }
          })
        );

        const lines = resolved.filter(Boolean);
        if (lines.length === 0) {
          this.hidden = true;
          return;
        }

        this.rows.innerHTML = '';
        lines.forEach((line) => this.rows.appendChild(this.buildRow(line)));
        this.hidden = false;
      }

      // Option label, e.g. "Packaging: 1 Gal Jug" ("" when there's no real option)
      optionLabel(product, variant) {
        if (!variant.title || variant.title === 'Default Title') return '';
        const first = (product.options || [])[0];
        const name = (first && (typeof first === 'string' ? first : first.name)) || 'Option';
        return `${name}: ${variant.title}`;
      }

      actionButtons(item) {
        const strings = window.savedForLaterStrings || {};
        const wrap = document.createElement('div');
        wrap.className = 'saved-for-later__actions';
        const move = document.createElement('button');
        move.type = 'button';
        move.className = 'saved-for-later__link link';
        move.textContent = strings.move || 'Move to cart';
        move.addEventListener('click', () => this.moveToCart(item, move));
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'saved-for-later__link saved-for-later__link--remove link';
        remove.textContent = strings.remove || 'Remove';
        remove.addEventListener('click', () => this.removeItem(item));
        wrap.append(move, remove);
        return wrap;
      }

      // Cart drawer: one narrow column, so no table cells competing for width.
      buildCompactRow({ item, product, variant }) {
        const tr = document.createElement('tr');
        tr.className = 'cart-item';
        const td = document.createElement('td');
        td.className = 'saved-for-later__compact';

        const link = document.createElement('a');
        link.href = `${product.url || '/products/' + product.handle}?variant=${variant.id}`;
        link.className = 'cart-item__name font-medium text-current break';
        link.textContent = product.title;
        td.appendChild(link);

        const meta = [this.optionLabel(product, variant), `Qty ${item.quantity}`].filter(Boolean);
        const sub = document.createElement('div');
        sub.className = 'product-option saved-for-later__meta';
        sub.textContent = meta.join(' · ');
        td.appendChild(sub);

        const foot = document.createElement('div');
        foot.className = 'saved-for-later__foot';
        const price = document.createElement('span');
        price.className = 'saved-for-later__price';
        price.textContent = this.money(variant.price * item.quantity);
        foot.append(price, this.actionButtons(item));
        td.appendChild(foot);

        tr.appendChild(td);
        return tr;
      }

      buildRow({ item, product, variant }) {
        if (this.dataset.compact === 'true') return this.buildCompactRow({ item, product, variant });
        const table = this.querySelector('.cart-items');
        const showMedia = table && !table.classList.contains('cart-items--no-media');
        const tr = document.createElement('tr');
        tr.className = 'cart-item';

        const url = `${product.url || '/products/' + product.handle}?variant=${variant.id}`;
        const image = variant.featured_image ? variant.featured_image.src : product.featured_image;

        if (showMedia) {
          const media = document.createElement('td');
          media.className = 'cart-item__media';
          if (image) {
            media.innerHTML = `<div class="cart-item__image-container gradient"><img src="${image.replace(
              /(\.[a-z]+)(\?.*)?$/i,
              '_300x$1'
            )}" class="cart-item__image" alt="" loading="lazy" width="150"></div>`;
          }
          tr.appendChild(media);
        }

        // Details: title + unit price + variant option
        const details = document.createElement('td');
        details.className = 'cart-item__details';
        const link = document.createElement('a');
        link.href = url;
        link.className = 'cart-item__name font-medium text-current break';
        link.textContent = product.title;
        const unit = document.createElement('div');
        unit.className = 'product-option product-option--price';
        unit.textContent = this.money(variant.price);
        details.append(link, unit);
        const label = this.optionLabel(product, variant);
        if (label) {
          const opt = document.createElement('div');
          opt.className = 'product-option';
          opt.textContent = label;
          details.appendChild(opt);
        }

        // Actions sit under the details — the totals cell is too narrow for them.
        details.appendChild(this.actionButtons(item));
        tr.appendChild(details);

        // Quantity (static text — quantity is edited once it's in the cart)
        const qty = document.createElement('td');
        qty.className = 'cart-item__quantity small-hide';
        const qtyText = document.createElement('span');
        qtyText.className = 'saved-for-later__qty';
        qtyText.textContent = item.quantity;
        qty.appendChild(qtyText);
        tr.appendChild(qty);

        // Line total
        const totals = document.createElement('td');
        totals.className = 'cart-item__totals right';
        const price = document.createElement('span');
        price.className = 'price price--end';
        price.textContent = this.money(variant.price * item.quantity);
        totals.appendChild(price);
        tr.appendChild(totals);

        return tr;
      }

      removeItem(item) {
        const stash = this.readStash();
        if (!stash) return;
        stash.items = stash.items.filter((i) => i.id !== item.id);
        this.writeStash(stash);
      }

      // Only allowed when the cart holds no conflicting entity; otherwise explain.
      async moveToCart(item, button) {
        const strings = window.savedForLaterStrings || {};
        const stash = this.readStash();
        if (!stash) return;
        button.disabled = true;
        try {
          const cart = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
            (r) => r.json()
          );
          const conflict = (cart.items || []).some(
            (i) => i.properties && i.properties._entity && i.properties._entity !== stash.entity
          );
          if (conflict) {
            button.disabled = false;
            button.textContent = strings.blocked || 'Finish your current order first';
            setTimeout(() => {
              button.textContent = strings.move || 'Move to cart';
            }, 3500);
            return;
          }
          const res = await fetch(`${window.routes.cart_url}/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: item.id, quantity: item.quantity, properties: item.properties }] }),
          });
          if (!res.ok) throw new Error('add failed');
          stash.items = stash.items.filter((i) => i.id !== item.id);
          this.writeStash(stash);
          window.location.reload();
        } catch (e) {
          button.disabled = false;
        }
      }
    }
  );
}
})();
